import {Injectable} from '@angular/core';
import {Observable, Subject} from 'rxjs';
import {map} from 'rxjs/operators';
import {Instance, LastOperation} from 'src/app/models/instance/instance';
import {ServerMessageResponse} from '../../models/server-message-response';
import {FabricService} from 'src/app/services/fabric/fabric.service';
import { Deployment } from 'src/app/models/deploy/deployment';
import { DeployStatus } from 'src/app/models/deploy/deploy-status';

const SERVICE_NAME = 'mgmt-instance-resources';

@Injectable({
  providedIn: 'root'
})
export class InstancesService {

  private refreshInstance = new Subject<void>();
  refresh$ = this.refreshInstance.asObservable();

  constructor(private fabricService: FabricService) {}

  private appliedConfigMap = new Map<string, boolean>();
  private agentDeployedMap = new Map<string, boolean>();

  static mapInstanceStatus(availabilityState: string, lifecycleState: string, lastLifecycleOperation?: LastOperation): DeployStatus {
    const opType = lastLifecycleOperation?.type;
    const opStatus = lastLifecycleOperation?.status?.toLowerCase();
    const lifecycle = lifecycleState?.toLowerCase();
    const availability = availabilityState?.toLowerCase();

    // Availability-driven transient states — stop/start never update lifecycleState
    if (availability === 'stopping') { return DeployStatus.STOPPING; }
    if (availability === 'starting') { return DeployStatus.STARTING; }
    if (availability === 'stopped')  { return DeployStatus.STOPPED; }

    // Operations that never update lifecycleState — detected via lastLifecycleOperation only
    if (opType === 'config_rvt_test' || opType === 'rvt_test' || opType === 'ef_test' || opType === 'sanity_test' || opType === 'inventory' || opType === 'backup_create' || opType === 'backup_delete' || opType === 'backup_update' || opType === 'cob_config' || opType === 'cob_reconfig' || opType === 'cob_update' || opType === 'cob_delete' || opType === 'runbook_provision' || opType === 'runbook_redeploy' || opType === 'runbook_config' || opType === 'runbook_update' || opType === 'runbook_delete' || opType === 'schedule_create' || opType === 'schedule_config_update' || opType === 'schedule_enable' || opType === 'schedule_disable' || opType === 'schedule_delete' || opType === 'scheduler_version_update' || opType === 'start_agent' || opType === 'stop_agent') {
      if (opStatus === 'inprogress') { return DeployStatus.INPROGRESS; }
      if (opStatus === 'failed')     { return DeployStatus.DEPLOYED; }
      if (opStatus === 'success')    { return DeployStatus.DEPLOYED; }
    }

    // Lifecycle-driven in-progress states
    if (lifecycle === 'initializing')             { return DeployStatus.INITIALIZING; }
    if (lifecycle === 'provisioning')             { return DeployStatus.DEPLOYING; }
    if (lifecycle === 'adopting')                 { return DeployStatus.ADOPTING; }
    if (lifecycle === 'configuring')              { return DeployStatus.CONFIGURING; }
    if (lifecycle === 'updating')                 { return DeployStatus.UPDATING; }
    if (lifecycle === 'resizing')                 { return DeployStatus.RESIZING; }
    if (lifecycle === 'restoring')                { return DeployStatus.RESTORING; }
    if (lifecycle === 'deleting')                 { return DeployStatus.TERMINATING; }
    if (lifecycle === 'failover_inprogress')      { return DeployStatus.FAILOVER_INPROGRESS; }
    if (lifecycle === 'pre_fallback_inprogress')  { return DeployStatus.PRE_FAILBACK_APPLYING; }
    if (lifecycle === 'fallback_inprogress')      { return DeployStatus.FALLBACK_INPROGRESS; }
    if (lifecycle === 'post_fallback_inprogress') { return DeployStatus.POST_FAILBACK_APPLYING; }

    // Success states — combine availabilityState + lastLifecycleOperation.type
    if (lifecycle === 'success') {
      if (availability === 'not_ready' && opType === 'initialize')                                        { return DeployStatus.INITIALIZED; }
      if (availability === 'not_ready' && opType === 'delete')                                            { return DeployStatus.TERMINATED; }
      if (availability === 'running' && (opType === 'provision' || opType === 'provisioning'
                                      || opType === 'redeploy' || opType === 'extend' || opType === 'adopt' || opType === 'start_agent' || opType === 'stop_agent'))                   { return DeployStatus.DEPLOYED; }
      if (availability === 'running' && opType === 'start')                                               { return DeployStatus.DEPLOYED; }
      if (availability === 'running' && opType === 'config')                                              { return DeployStatus.CONFIGURED; }
      if (availability === 'running' && (opType === 'update' || opType === 'infraUpdate'
                                      || opType === 'update_base_image'))                                 { return DeployStatus.UPDATED; }
      if (availability === 'running' && (opType === 'failover' || opType === 'pre_fallback'
                                      || opType === 'fallback' || opType === 'post_fallback'))            { return DeployStatus.DEPLOYED; }
      if (availability === 'running')                                                                      { return DeployStatus.DEPLOYED; }
      return DeployStatus.DEPLOYED;
    }

    // Lifecycle-failed states
    if (lifecycle === 'failed') {
      switch (opType) {
        case 'initialize':                                      return DeployStatus.INITIALIZE_FAILED;
        case 'provision':
        case 'provisioning':
        case 'redeploy':
        case 'adopt':
        case 'extend':                                          return DeployStatus.FAILED;
        case 'config':                                          return DeployStatus.CONFIGURATION_FAILED;
        case 'update':
        case 'infraUpdate':
        case 'update_base_image':                               return DeployStatus.UPDATE_FAILED;
        case 'failover':                                        return DeployStatus.FAILOVER_FAILED;
        case 'pre_fallback':                                    return DeployStatus.PRE_FAILBACK_FAILED;
        case 'fallback':                                        return DeployStatus.FAILBACK_FAILED;
        case 'post_fallback':                                   return DeployStatus.POST_FAILBACK_FAILED;
        case 'delete':                                          return DeployStatus.TERMINATION_FAILED;
        default:                                                return DeployStatus.FAILED;
      }
    }

    // Stop/start failures: backend sets availability=not_ready without updating lifecycleState
    if (availability === 'not_ready' && opStatus === 'failed') {
      if (opType === 'stop')  { return DeployStatus.STOP_FAILED; }
      if (opType === 'start') { return DeployStatus.START_FAILED; }
    }

    return DeployStatus.FAILED;
  }

  private applyStatusMapping(instance: Instance): Instance {
    if (instance?.availabilityState || instance?.lifecycleState) {
      instance.availabilityState = instance.availabilityState?.toLowerCase();
      instance.status = InstancesService.mapInstanceStatus(
        instance.availabilityState,
        instance.lifecycleState,
        instance.lastLifecycleOperation
      );
    }
    return instance;
  }

  /**
   * instance's config is currently applied
   * @param instanceId The ID of the instance
   */
  setConfigApplied(instanceId: string): void {
    this.appliedConfigMap.set(instanceId, true);
    setTimeout(() => {
      this.appliedConfigMap.delete(instanceId);
    }, 60000);
  }

  /**
   * Check if an instance's config is currently applied (within 1 min)
   * @param instanceId The ID of the instance
   * @returns boolean
   */
  isConfigApplied(instanceId: string): boolean {
    return this.appliedConfigMap.has(instanceId);
  }

  // marks instance as agent-deployed and auto-clears after 60s to show success banner transiently
  setAgentDeployed(instanceId: string): void {
    this.agentDeployedMap.set(instanceId, true);
    setTimeout(() => {
      this.agentDeployedMap.delete(instanceId);
    }, 60000);
  }

  // checks if instance was recently marked as agent-deployed within the 60s window
  isAgentDeployed(instanceId: string): boolean {
    return this.agentDeployedMap.has(instanceId);
  }

  triggerRefresh(): void {
    this.refreshInstance.next();
  }

  getInstancesForBlueprint(blueprintId: string, tenantId?: string, size?: any): Observable<Instance[]> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'get_mgmt_instances', {blueprintId, tenantId, size})
      .pipe(map(response => {
        if (response?.data) { response.data = response.data.map((i: Instance) => this.applyStatusMapping(i)); }
        return response;
      }));
  }

  getInstancesForSolution(solutionId?: string, blueprintId?: string, tenantId?: string, size?: any): Observable<Instance[]> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'get_mgmt_instances', {solutionId, blueprintId, tenantId, size})
      .pipe(map(response => {
        if (response?.data) { response.data = response.data.map((i: Instance) => this.applyStatusMapping(i)); }
        return response;
      }));
  }

  getInstances(instanceObj?): Observable<any> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'get_mgmt_instances', instanceObj)
      .pipe(map(response => {
        if (response?.data) { response.data = response.data.map((i: Instance) => this.applyStatusMapping(i)); }
        return response;
      }));
  }

  getInstanceDetails(id): Observable<any> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'get_mgmt_instances_id', {id})
      .pipe(map(instance => this.applyStatusMapping(instance)));
  }

  getInstanceById(id: string): Observable<Instance> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'get_mgmt_instances_id', {id})
      .pipe(map(instance => this.applyStatusMapping(instance)));
  }

  addInstance(id): Observable<any> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'post_mgmt_instance-register', id);
  }

  getInstanceDetailsById(instanceId): Observable<any> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'get_mgmt_instances_instanceid_details', {instanceId});
  }

  deleteInstance(id: string): Observable<ServerMessageResponse> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'delete_mgmt_instances_id', {id});
  }

  deployInstance(deployment: Deployment): Observable<Deployment> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'post_mgmt_instances', deployment);
  }

  redeployInstance(id: string): Observable<ServerMessageResponse> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'put_mgmt_instances_id_redeploy', {id});
  }

  reinitInstance(id: string): Observable<ServerMessageResponse> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'put_mgmt_instances_id_re-init', {id});
  }

  getInstanceParameters(id: string): Observable<any> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'get_mgmt_instances_id_parameters', {id});
  }

  putInstanceParameters(id: string, parameters): Observable<any> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'put_mgmt_instances_id_parameters', {id, parameters});
  }

  putBuildVersion(versionForm: any ): Observable<any> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'put_mgmt_instances_id_update',
    versionForm);
  }

  putUpdateVerification(data: any ): Observable<any> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'put_mgmt_instances_id_update-verification', data);
  }

  getInstanceDetailsSftpDetailsById(id?, direction?, status?): Observable<any> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'get_mgmt_instances_id_ft-route', {id, direction, status});
  }

  getInstanceDetailsSftpDetailsByIdByRouteID(id, routeId): Observable<any> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'get_mgmt_instances_id_ft-route_routeid', {id, routeId});
  }

  postInstanceDetailsSftpDetailsById(obj?): Observable<any> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'post_mgmt_instances_id_ft-route', obj);
  }

  putInstanceDetailsSftpDetailsByIdByRouteID(obj?): Observable<any> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'put_mgmt_instances_id_ft-route_routeid', obj);
  }

  sendingLogBasedOnSelection(logDetails?): Observable<any> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'put_mgmt_instances_id', logDetails);
  }

  deleteInstanceDetailsSftpRoute(id, routeId): Observable<any> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'delete_mgmt_instances_id_ft-route_routeid', {id,routeId});
  }

  getTestResults(id, size?: Number): Observable<any> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'get_mgmt_instances_id_test-results', {id, size});
  }

  putEFTest(id): Observable<any> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'put_mgmt_instances_id_ef-test', {id});
  }

  putSanityTest(id): Observable<any> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'put_mgmt_instances_id_sanity-test', {id});
  }

  putFailover(id, drMode?: string): Observable<any> {
    const payload: any = {id};
    if (drMode) {
      payload.drMode = drMode;
    }
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'put_mgmt_instances_id_failover', payload);
  }

  putPreFallback(id): Observable<any> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'put_mgmt_instances_id_pre-fallback', {id});
  }

  putFallback(id, drMode?: string): Observable<any> {
    const payload: any = {id};
    if (drMode) {
      payload.drMode = drMode;
    }
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'put_mgmt_instances_id_fallback', payload);
  }

  putPostFallback(id): Observable<any> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'put_mgmt_instances_id_post-fallback', {id});
  }

  postSshKey(obj): Observable<any> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'post_mgmt_instances_id_ft-route_routeid_users_username_ssh-keys', obj);
  }

  putSshKey(obj): Observable<any> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'put_mgmt_instances_id_ft-route_routeid_users_username_ssh-keys_keyname', obj);
  }

  deleteSshKey(obj): Observable<any> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'delete_mgmt_instances_id_ft-route_routeid_users_username_ssh-keys_keyname', obj);
  }

  applyLocalConfig(id: string, type: any): any {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'put_mgmt_instances_id_local-config', {id, type});
  }

  applyDefinition(blueprintVersionId: string, instanceId: string, sizingDocumentId?: any): Observable<Instance> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'put_mgmt_instances_id_extend', {blueprintVersionId, id: instanceId, sizingDocumentId});
  }

  agentStart(data?: Instance): any {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'put_mgmt_instances_id_start-agent', {id: data.id});
  }

  agentStop(data?: Instance): any {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'put_mgmt_instances_id_stop-agent', {id: data.id});
  }

  instanceStart(data?: Instance): any {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'put_mgmt_instances_id_start', {id: data.id});
  }

  instanceStop(data?: Instance): any {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'put_mgmt_instances_id_stop', {id: data.id});
  }

  instanceSchedulerStart(data?: Instance): any {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'put_mgmt_instances_id_start', data);
  }

  instanceSchedulerStop(data?: Instance): any {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'put_mgmt_instances_id_stop', data);
  }

  getInstanceDatabases(id): any {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'get_mgmt_instances_id_databases', {id});
  }

  putInstanceProvision(obj): Observable<any> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'put_mgmt_instances_id_provision', obj);
  }

  executeRVTState(instanceId: string, configurationId: string,): Observable<void> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'put_mgmt_instances_id_rvt-test',
    {id: instanceId, configurationId});
  }

  getEncryptionKeys(id: string): Observable<any> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'get_mgmt_instances_id_ft-enc-keys', {id});
  }

  postEncryptionKey(obj): Observable<any> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'post_mgmt_instances_id_ft-enc-keys', obj);
  }

  updateEncryptionKey(obj): Observable<any> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'put_mgmt_instances_id_ft-enc-keys_keyname', obj);
  }

  getInstanceUsageSummarybyId(id:string, month?:string, from?:string, to?:string): Observable<any> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'get_mgmt_instances_id_usage-summary', {id, month, from, to});
  }

  updateInfraVersion(obj): Observable<any> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'put_mgmt_instances_id_infra-update', obj);
  }

   postCheckInfraUpdates(id: string, solutionVersion: string): Observable<any> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'post_mgmt_instances_id_infra-update_dry-run', {id, solutionVersion});
  }

  getInstanceDependencyTree(id: string): Observable<any> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'get_mgmt_instances_id_dependency-tree', {id});
  }

   solutionUpgradeScheduler(versionForm: any ): Observable<any> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'put_mgmt_instances_id_update',
    versionForm);
  }

  putInstanceAbort(id: string, forceAbort: boolean = false): Observable<any> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'put_mgmt_instances_id_abort', {id, forceAbort});
  }

  // triggers agent adoption for an external tenant instance
  postInstanceAdopt(id: string): Observable<any> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'post_mgmt_instances_id_adopt', {id});
  }
}
